import { useState, useEffect, useRef } from "react";
import translateStrings from "../components/AI/translateStrings";
import { collectTranslatableTexts, applyTranslations } from "../helpers/translations/translatableStrings";

/**
 * Vertaalt een set strings naar de gevraagde taal.
 *
 * De hele set gaat in één aanroep naar de `translateTexts` Cloud Function, die
 * ontdubbelt en eerdere vertalingen cachet. Daarvoor deed dit per string een
 * eigen call: een vragenlijst met veel vragen en antwoordopties kostte zo 100+
 * round trips per taalwissel.
 *
 * De deelnemer op de open vragenlijst kan zelf van taal wisselen, dus `loading`
 * moet ook bij een tweede en derde ronde weer op true staan — anders is er niets
 * te zien terwijl de vertaling loopt.
 *
 * @param {Record<string, string|object>} strings - Te vertalen strings, per sleutel
 * @param {string} languageCode - Doeltaal, bijv. 'tr'
 * @returns {{translations: Record<string, any>, loading: boolean, language: string|null}}
 *          `language` is de taal waar de huidige `translations` bij horen
 */
const useTranslations = (strings, languageCode = "en") => {
    const [result, setResult] = useState({ translations: {}, language: null });
    const [loading, setLoading] = useState(true);

    // Volgnummer van de laatst gestarte ronde: een tragere, oudere ronde mag de
    // inmiddels gekozen taal niet meer overschrijven.
    const requestRef = useRef(0);

    useEffect(() => {
        const requestId = ++requestRef.current;

        // Niets te vertalen (o.a. de Nederlandse snelweg): de vorige vertalingen
        // moeten weg, anders blijft de vragenlijst in de vórige taal staan.
        if (!strings || Object.keys(strings).length === 0) {
            setResult({ translations: {}, language: languageCode });
            setLoading(false);
            return;
        }

        setLoading(true);

        const fetchTranslations = async () => {
            const texts = collectTranslatableTexts(strings);

            let translations;
            try {
                translations = await translateStrings({ texts, languageCode });
            } catch (error) {
                // Zonder vertaling toont de vragenlijst zijn eigen Nederlandse
                // teksten. Blijven laden zou de deelnemer met een spinner
                // achterlaten die nooit stopt, dus hier stoppen we luidruchtig.
                console.error('Vertalen van de vragenlijst mislukt:', error);
                if (requestRef.current !== requestId) return;
                setResult({ translations: {}, language: languageCode });
                setLoading(false);
                return;
            }

            if (requestRef.current !== requestId) return;

            setResult({ translations: applyTranslations(strings, translations), language: languageCode });
            setLoading(false);
        };

        fetchTranslations();
    }, [strings, languageCode]);

    return { translations: result.translations, loading, language: result.language };
};

export default useTranslations;
